import { Dataset } from 'generic/dataset'
import { DefaultImagePreferences } from './constants'

export interface UserImagePreference {
  key: string
  value: string
  description: string
  options: any
  is_default: boolean
}

export const loadUserImagePreferences = (communityId: string): Promise<UserImagePreference[]> => {
  return new Dataset()
    .loadCommunityDataset('userAiPreferences', communityId, [
      { param: 'is_active', value: 1 },
      { param: 'preference_type', value: 'image' }
    ])
    .then((response) => {
      const preferences = response[0] || []
      
      // Transform preferences to match expected format
      const transformedPreferences: UserImagePreference[] = preferences.map((pref: any) => ({
        key: pref.id,
        value: pref.id,
        description: pref.name,
        options: typeof pref.options === 'string' ? JSON.parse(pref.options) : pref.options,
        is_default: pref.is_default
      }))

      // Always add a "Custom" option
      transformedPreferences.push({
        key: 'custom',
        value: 'custom',
        description: 'Custom',
        options: null,
        is_default: false
      })

      return transformedPreferences
    })
    .catch(() => {
      // Return custom option if loading fails
      return [{
        key: 'custom',
        value: 'custom',
        description: 'Custom',
        options: null,
        is_default: false
      }]
    })
}

export const getDefaultImagePreference = (preferences: UserImagePreference[]): UserImagePreference => {
  // Find the default preference
  const defaultPref = preferences.find(pref => pref.is_default)
  
  if (defaultPref) {
    return defaultPref
  }

  // If no default found, return first preference or custom
  const firstPref = preferences.find(pref => pref.value !== 'custom')
  
  if (firstPref) {
    return firstPref
  }

  // Return custom with default options
  return {
    key: 'custom',
    value: 'custom', 
    description: 'Custom',
    options: DefaultImagePreferences,
    is_default: false
  }
}

export const buildImagePreferencesPayload = (preferences: UserImagePreference, customOptions?: any) => {
  const useCustomPreferences = preferences.value === 'custom'
  const options = useCustomPreferences ? (customOptions || DefaultImagePreferences) : preferences.options

  return {
    style: options.style || DefaultImagePreferences.style,
    quality: options.quality || DefaultImagePreferences.quality,
    subjectFocus: options.subjectFocus || DefaultImagePreferences.subjectFocus,
    colorScheme: options.colorScheme || DefaultImagePreferences.colorScheme,
    mood: options.mood || DefaultImagePreferences.mood,
    complexity: options.complexity || DefaultImagePreferences.complexity,
    artStyle: options.artStyle || DefaultImagePreferences.artStyle,
    lighting: options.lighting || DefaultImagePreferences.lighting,
  }
}